import React, { useEffect, useState, useRef } from 'react';
import { ChevronDownIcon, CheckIcon } from 'lucide-react';
interface DropdownOption {
  value: string;
  label: string;
}
interface CustomDropdownProps {
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  darkMode: boolean;
  icon: React.ReactNode;
  placeholder?: string;
  required?: boolean;
}
export const CustomDropdown: React.FC<CustomDropdownProps> = ({
  value,
  options,
  onChange,
  darkMode,
  icon,
  placeholder = 'Select an option',
  required = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Get the selected option label for display
  const selectedOption = options.find(option => option.value === value);
  const displayValue = selectedOption ? selectedOption.label : placeholder;
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Handle selection
  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };
  return <div className="relative" ref={dropdownRef} data-id="element-1069">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1070">
        {icon}
      </div>
      {/* Dropdown trigger button */}
      <button type="button" onClick={() => setIsOpen(!isOpen)} className={`w-full pl-10 pr-10 py-2.5 rounded-lg text-sm text-left flex items-center justify-between ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white hover:bg-navy-800' : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50'} border transition-colors duration-200 cursor-pointer`} aria-haspopup="listbox" aria-expanded={isOpen} data-id="element-1071">
        <span className={value ? '' : 'text-gray-500'} data-id="element-1072">{displayValue}</span>
        <ChevronDownIcon className="h-5 w-5 text-gray-400" data-id="element-1073" />
      </button>
      {/* Dropdown menu */}
      {isOpen && <div className={`absolute z-10 mt-1 w-full rounded-lg shadow-lg ${darkMode ? 'bg-navy-800 border border-navy-700' : 'bg-white border border-gray-300'}`} data-id="element-1074">
          <ul className="py-1 max-h-60 overflow-auto text-sm rounded-lg focus:outline-none" role="listbox" data-id="element-1075">
            {options.map(option => <li key={option.value} className={`px-4 py-2.5 cursor-pointer flex items-center ${option.value === value ? darkMode ? 'bg-navy-700 text-white' : 'bg-blue-50 text-blue-900' : darkMode ? 'text-white hover:bg-navy-700' : 'text-gray-900 hover:bg-gray-100'}`} role="option" aria-selected={option.value === value} onClick={() => handleSelect(option.value)} data-id="element-1076">
                {option.value === value && <CheckIcon className="h-4 w-4 text-deep-pink mr-2" data-id="element-1077" />}
                <span className={option.value === value ? 'ml-0' : 'ml-6'} data-id="element-1078">
                  {option.label}
                </span>
              </li>)}
          </ul>
        </div>}
      {/* Hidden select for form validation */}
      {required && <select className="sr-only" aria-hidden="true" tabIndex={-1} value={value} onChange={() => {}} required data-id="element-1079">
          <option value="" data-id="element-1080">{placeholder}</option>
          {options.map(option => <option key={option.value} value={option.value} data-id="element-1081">
              {option.label}
            </option>)}
        </select>}
    </div>;
};